/**
 * Daily Activity entry modal — "today only" guided flow layered on the SAME
 * GET/POST /stats/measurements|daily paths DailyEntryTab uses. Reachable
 * three ways, all via useDailyActivityModalStore: a reminder notification
 * click (self, editable), the manual "Log Daily Activity" button (self,
 * editable), or a "shared with trainer" notification click (a `clientId` is
 * set → read-only view of that client's entry, GET-scoped through
 * StatsAccessService). DailyEntryTab remains the surface for backfill and
 * trainer-for-client BACKFILL entry — this modal never writes on a client's
 * behalf.
 */
import type { ApiMetricDefinition } from '../../api/types/stats.types';
import type { DailyActivityPeriod } from '../../store/useDailyActivityModalStore';
import type { MetricUnitSettings } from '../../utils/metricUnits';

import { FormSubmitButton, FormProvider, useForm } from '@mfw/forms';
import {
  useBooleanState,
  ModalHeader,
  ModalFooter,
  SwitchField,
  Modal,
} from '@mfw/components';
import type { ModalRefType } from '@mfw/components';
import Typography from '@mui/material/Typography';
import { isDefined } from '@mfw/common';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import React from 'react';

import {
  dailyMetricFieldTestId,
  metricCategoryMessages,
  getMetricMessage,
  DailyMetricField,
  todayIso,
} from '../Stats';
import {
  useRecordMeasurements,
  useDaily,
} from '../../hooks/stats/measurements';
import Tabs from '../../components/Tabs';
import {
  EVENING_KEYS_BY_CATEGORY,
  EVENING_CATEGORY_TABS,
  WORKOUT_BREAKDOWN_METRIC_KEY,
  EVENING_KEYS,
  MORNING_KEYS,
} from './dailyActivityKeys';
import { useDailyActivityModalStore } from '../../store/useDailyActivityModalStore';
import { toCanonical, toDisplay, displayUnit } from '../../utils';
import { useMetricRegistry } from '../../hooks/stats/registry';
import { useAuthContext, useSnackbar } from '../../context';
import messages from './messages';

type FormValues = Record<string, boolean | null | number | string>;

const WorkoutPortionHint = ({
  breakdownKey,
  registry,
  settings,
  daily,
}: {
  breakdownKey: keyof typeof WORKOUT_BREAKDOWN_METRIC_KEY;
  registry: ReturnType<typeof useMetricRegistry>;
  settings: MetricUnitSettings;
  daily: ReturnType<typeof useDaily>['data'];
}) => {
  const { formatMessage } = useIntl();
  const breakdown = daily?.workoutBreakdown?.[breakdownKey];
  const session = breakdown && 'session' in breakdown ? breakdown.session : 0;
  if (!session) return null;
  const def = registry.byKey(WORKOUT_BREAKDOWN_METRIC_KEY[breakdownKey]);
  if (!def) return null;
  const displayValue = Math.round(toDisplay(session, def, settings));
  return (
    <Typography color="text.secondary" variant="caption">
      {formatMessage(messages.fromWorkout, { value: displayValue })}
    </Typography>
  );
};

// Trainer view of a client's shared entry — a plain label/value line, no
// input widget (the trainer never edits a client's own Daily Activity entry
// from here; DailyEntryTab is the write surface for trainer-for-client).
const ReadOnlyMetricValue = ({
  def,
  settings,
  value,
}: {
  def: ApiMetricDefinition;
  settings: MetricUnitSettings;
  value: FormValues[string];
}) => {
  const { formatMessage } = useIntl();
  const message = getMetricMessage(def.key);
  const label = message ? formatMessage(message) : def.key;
  const unit = displayUnit(def, settings);
  const displayValue = isDefined(value)
    ? `${value}${unit ? ` ${unit}` : ''}`
    : '—';
  return (
    <Box display="flex" justifyContent="space-between" alignItems="baseline">
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
      <Typography
        data-testid={dailyMetricFieldTestId('daily-activity-view', def.key)}
        variant="body1"
      >
        {displayValue}
      </Typography>
    </Box>
  );
};

const MetricFieldWithHint = ({
  def,
  settings,
  registry,
  daily,
  readOnly,
  value,
}: {
  def: ApiMetricDefinition;
  settings: MetricUnitSettings;
  registry: ReturnType<typeof useMetricRegistry>;
  daily: ReturnType<typeof useDaily>['data'];
  readOnly: boolean;
  value: FormValues[string];
}) => {
  const breakdownKey = (
    Object.entries(WORKOUT_BREAKDOWN_METRIC_KEY) as Array<
      [keyof typeof WORKOUT_BREAKDOWN_METRIC_KEY, string]
    >
  ).find(([, metricKey]) => metricKey === def.key)?.[0];
  return (
    <Box>
      {readOnly ? (
        <ReadOnlyMetricValue def={def} settings={settings} value={value} />
      ) : (
        <DailyMetricField
          testIdPrefix="daily-activity"
          settings={settings}
          def={def}
        />
      )}
      {breakdownKey && (
        <WorkoutPortionHint
          breakdownKey={breakdownKey}
          registry={registry}
          settings={settings}
          daily={daily}
        />
      )}
    </Box>
  );
};

const FieldGrid = ({
  keys,
  registry,
  settings,
  daily,
  readOnly,
  values,
}: {
  keys: string[];
  registry: ReturnType<typeof useMetricRegistry>;
  settings: MetricUnitSettings;
  daily: ReturnType<typeof useDaily>['data'];
  readOnly: boolean;
  values: FormValues;
}) => (
  <Box display="flex" flexDirection="column" gap={2} mb={2}>
    {keys.map((key) => {
      const def = registry.byKey(key);
      if (!def) return [];
      return (
        <Box key={key} width="100%">
          <MetricFieldWithHint
            registry={registry}
            settings={settings}
            daily={daily}
            def={def}
            readOnly={readOnly}
            value={values[key]}
          />
        </Box>
      );
    })}
  </Box>
);

const MorningFields = ({
  registry,
  settings,
  daily,
  readOnly,
  values,
}: {
  registry: ReturnType<typeof useMetricRegistry>;
  settings: MetricUnitSettings;
  daily: ReturnType<typeof useDaily>['data'];
  readOnly: boolean;
  values: FormValues;
}) => {
  return (
    <Box pt={1}>
      <FieldGrid
        keys={MORNING_KEYS}
        registry={registry}
        settings={settings}
        daily={daily}
        readOnly={readOnly}
        values={values}
      />
    </Box>
  );
};

const EveningFields = ({
  registry,
  settings,
  daily,
  readOnly,
  values,
}: {
  registry: ReturnType<typeof useMetricRegistry>;
  settings: MetricUnitSettings;
  daily: ReturnType<typeof useDaily>['data'];
  readOnly: boolean;
  values: FormValues;
}) => (
  <Tabs initialValue={EVENING_CATEGORY_TABS[0]}>
    <Tabs.TabsList label="Daily Activity categories" centered>
      {EVENING_CATEGORY_TABS.map((category) => (
        <Tabs.Tab
          data-testid={`daily-activity-tab-${category}`}
          label={<FormattedCategoryLabel category={category} />}
          value={category}
          key={category}
        />
      ))}
    </Tabs.TabsList>
    <>
      {EVENING_CATEGORY_TABS.map((category) => (
        <Tabs.TabPanel value={category} key={category}>
          <FieldGrid
            keys={EVENING_KEYS_BY_CATEGORY[category] ?? []}
            registry={registry}
            settings={settings}
            daily={daily}
            readOnly={readOnly}
            values={values}
          />
        </Tabs.TabPanel>
      ))}
    </>
  </Tabs>
);

const CATEGORY_MESSAGES = metricCategoryMessages as Record<
  string,
  (typeof metricCategoryMessages)[keyof typeof metricCategoryMessages]
>;

const FormattedCategoryLabel = ({ category }: { category: string }) => {
  const { formatMessage } = useIntl();
  const message = CATEGORY_MESSAGES[category];
  return <>{message ? formatMessage(message) : category}</>;
};

export const DailyActivityModal = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const settings = currentUser?.settings as MetricUnitSettings;
  const modalRef = React.useRef<ModalRefType>(null);
  const store = useDailyActivityModalStore();
  const registry = useMetricRegistry();
  const isSaving = useBooleanState();

  const date = store.date ?? todayIso();
  const period: DailyActivityPeriod = store.period ?? 'morning';
  const keys = period === 'morning' ? MORNING_KEYS : EVENING_KEYS;
  const isTrainerView = !!store.clientId;

  const daily = useDaily({
    clientId: store.clientId ?? undefined,
    date,
    enabled: store.isOpen,
  });

  // `byKey` (not the whole `registry` wrapper, which is a new object every
  // render) — the registry and the daily entry are independent requests, and
  // when the entry lands first every key resolves to null; the memo has to
  // re-run once the definitions arrive or the form stays blank (A3-3).
  const { byKey: registryByKey } = registry;
  const initialValues = React.useMemo(() => {
    const values: FormValues = { shareWithTrainer: false };
    const byKey = new Map(
      (daily.data?.metrics ?? []).map((m) => [m.key, m.value]),
    );
    for (const key of keys) {
      const def = registryByKey(key);
      const canonical = byKey.get(key);
      values[key] =
        !def || !isDefined(canonical)
          ? null
          : Math.round(toDisplay(canonical, def, settings) * 100) / 100;
    }
    return values;
  }, [daily.data, keys, registryByKey, settings]);

  const methods = useForm<FormValues>({ defaultValues: initialValues });
  const formId = React.useId();

  React.useEffect(() => {
    methods.reset(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  React.useEffect(() => {
    if (store.isOpen) {
      modalRef.current?.open();
    } else {
      modalRef.current?.close();
    }
  }, [store.isOpen]);

  const { mutateAsync: recordAsync } = useRecordMeasurements();

  const handleSubmit = async (values: FormValues) => {
    const changed = [];
    const filled = [];
    for (const key of keys) {
      const def = registry.byKey(key);
      const raw = values[key];
      const parsed =
        raw === '' || !isDefined(raw) || Number.isNaN(Number(raw))
          ? null
          : Number(raw);
      if (!def || !isDefined(parsed)) continue;
      const sample = {
        businessDate: date,
        metricKey: def.key,
        value: toCanonical(parsed, def, settings),
      };
      filled.push(sample);
      if (parsed !== (initialValues[key] as null | number)) {
        changed.push(sample);
      }
    }
    // "Share with your trainer" is a reason to submit on its own: a re-opened
    // entry prefills every value, so nothing "changed" — re-sending the
    // filled values is harmless (the backend upsert is idempotent) and is the
    // only way the share flag reaches the API (sen-13 Z3).
    const samples =
      changed.length || !values.shareWithTrainer ? changed : filled;
    if (!samples.length) {
      enqueueSnackbar({
        message: formatMessage(messages.noChanges),
        variant: 'info',
      });
      return;
    }
    isSaving.on();
    try {
      await recordAsync({
        period,
        samples,
        shareWithTrainer: !!values.shareWithTrainer,
      });
      enqueueSnackbar({
        message: formatMessage(messages.saved),
        variant: 'success',
      });
      store.close();
    } finally {
      isSaving.off();
    }
  };

  const title =
    period === 'morning'
      ? formatMessage(messages.morningTitle)
      : formatMessage(messages.eveningTitle);

  return (
    <FormProvider id={formId} onSubmit={handleSubmit} {...methods}>
      <Modal
        onClose={store.close}
        variant="small"
        mobileFullscreen
        ref={modalRef}
      >
        <ModalHeader>
          <Typography variant="h5">{title}</Typography>
        </ModalHeader>
        {period === 'morning' ? (
          <MorningFields
            registry={registry}
            settings={settings}
            daily={daily.data}
            readOnly={isTrainerView}
            values={initialValues}
          />
        ) : (
          <EveningFields
            registry={registry}
            settings={settings}
            daily={daily.data}
            readOnly={isTrainerView}
            values={initialValues}
          />
        )}
        {!isTrainerView && (
          <ModalFooter>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Controller
                render={({ field }) => (
                  <SwitchField
                    onChange={(_event, { checked }) => field.onChange(checked)}
                    data-testid="daily-activity-share-with-trainer"
                    label={formatMessage(messages.shareWithTrainer)}
                    checked={!!field.value}
                    name={field.name}
                  />
                )}
                name="shareWithTrainer"
                control={methods.control}
              />
              <FormSubmitButton
                data-testid="daily-activity-save"
                disabled={isSaving.isActive || daily.isLoading}
                form={formId}
              >
                {formatMessage(messages.saveButton)}
              </FormSubmitButton>
            </Box>
          </ModalFooter>
        )}
      </Modal>
    </FormProvider>
  );
};

export default DailyActivityModal;
